/**
* API Fetch Controller
* 
* Header['Cache-Control': 's-maxage=60, stale-while-revalidate']
* If this header is present, we cache requests
*
* If Header Authorization not 'Public'
* For Critical Data with caching
* If this header is present, we DISABLE the isr and we leave only server caching
*
* customOptions.errorToast - Default TRUE
* customOptions.declarativeFetch === true - Force Use useAsyncData or useFetch
* customOptions.imperativeFetch === true - Force Use $fetch
*/
import { defu } from 'defu'

export function useCustomFetch(url, options = {}, customOptions = {}) {
  const { payload: payloadData, static: staticData } = useNuxtApp()
  
  const { confGlobal } = useConfig()
  const { requestHeaders } = useApi()

  const baseUrl = `${confGlobal.apiProxyUrl}${url}`
  const method = options.method?.toUpperCase() || 'GET'
  const isGet = method === 'GET'

  if (!options.headers) {
    options.headers = {}
  }

  /*
  * If isGet
  */
  if (isGet) {
    // Default maxAge
    if (customOptions?.maxAge !== false && customOptions?.maxAge !== 0) {
      customOptions.maxAge = 30
    }
  }

  /*
  * Headers
  */
  const headers = options.headers ? requestHeaders(options.headers, true) : requestHeaders({}, true)
  options.headers = headers

  /*
  * Caching Key
  */
  const keyPrefix = headers['Authorization'] !== 'Public' ? `customer_${hashKey(headers['Authorization'], 6)}_${headers['Admin-Token'] ? '_' + hashKey(headers['Admin-Token'], 6) : ''}` : 'public'
  const keySufix = hashKey(`${method}-${url}-${JSON.stringify(options.query?.value || options.query)}-${JSON.stringify(options.params?.value || options.params)}`, 12)
  const key = options.key || `${keyPrefix}:${keySufix}`

  /*
  * Default Options
  */
  const defaultOptions = {
    method,
    headers,
    key: key,
    cache: isGet && customOptions?.maxAge ? 'default' : 'no-cache',
    // server: $member?.user?.value?.email?.includes('@ipmedia.net') && useCustomCookie('disableCache').value ? false : true,
    server: false,
    ...(() => {
      if (isGet) {
        return {
          deep: true
        }
      }
    })(),
    lazy: false,
    watch: false,
    credentials: 'omit',
    default: () => [],
    transform(input) {
      const userTransform = typeof options.transform === 'function' ? options.transform : (x) => x
      const transformed = userTransform(input)

      if (import.meta.client) {
        return {
          ...transformed,
          ...{ __ssr: false },
          fetchedAt: new Date()
        }
      }

      return {
        ...transformed,
        ...{ __ssr: true },
        fetchedAt: new Date()
      }
    },
    getCachedData(key) {
      const reqParams = options.query?.value || options.query || options.params?.value || options.params
      const data = payloadData.data[key] || staticData.data[key]
      const params = parseFlatParams(data?._meta?.links?.current)

      let stopCaching = false
      if (reqParams) {
        const filteredQuery = filterNonEmptyParams(reqParams)

        for (const key in params) {
          const a = filteredQuery[key]
          const b = params[key]

          const normA = a != null ? String(a) : null
          const normB = b != null ? String(b) : null

          if (normA !== normB) {
            stopCaching = true
          }
        }
      }

      if (!data || !customOptions?.maxAge || stopCaching) {
        return
      }

      const expirationDate = new Date(data.fetchedAt)
  
      expirationDate.setTime(expirationDate.getTime() + customOptions?.maxAge * 1000)

      const isExpired = expirationDate.getTime() < Date.now()

      if (isExpired) {
        return
      }

      return data
    },
    onResponseError({ response }) {  
      const msgArr = response?._data?.data
      const htmlMsg = arrayToHtmlList(msgArr)

      const errorMessage = htmlMsg ?
        htmlMsg
        : response?._data?.message
          ? response?._data?.message
          : response?.statusText || 'An error occurred'

      if (errorMessage && customOptions.errorToast !== false) {        
        useEventQueue().add('toast', { severity: 'error', detail: errorMessage, life: 5000, group: 'html' })
      }

      // Return Error
      throw createError({
        statusCode: response.status,
        message: response?._data?.message || 'An error occurred',
        data: response?._data
      })
    }
  }

  // Merge Objects
  const mergedOptions = defu(options, defaultOptions)

  // Params for useAsyncData
  const asyncDataOptions = {
    transform: mergedOptions.transform,
    key: mergedOptions.key,
    onResponseError: mergedOptions.onResponseError
  }

  // 🧠 Critical: SSR + GET → use useAsyncData (automatic payload)
  if (import.meta.server && isGet) {
    return useAsyncData(key, () =>
      $fetch(baseUrl, mergedOptions),
    asyncDataOptions
    )
  }

  // 🧠 On the client or for POST/DELETE — use $fetch directly
  if (((!isGet && import.meta.client) || customOptions.imperativeFetch) && customOptions.declarativeFetch !== true) {
    return $fetch(baseUrl, mergedOptions)
  }

  // 🧠 On the client or for POST — standard useFetch
  return useFetch(baseUrl, mergedOptions)
}

/**
* Invalidate Cache
* Example:
* useInvalidateCache('customers/orders')
*/
export function useInvalidateCache(url) {
  const { requestHeaders } = useApi()

  return $fetch(`/api/invalidate${url}`, {
    method: 'delete',
    headers: requestHeaders({}, true)
  })
}